import React, { useEffect, useMemo, useState } from 'react';
import { Button, Pagination, Table } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { FilterValue, SorterResult } from 'antd/es/table/interface';
import { FileTextOutlined, FilterFilled, SearchOutlined } from '@ant-design/icons';
import type {
    BoardPageVo,
    BoardQuery,
    BoardSummaryVo,
    TAttentionCard,
    TBillingApiScope,
    TBoardRow,
    TBoardSort,
    TBoardTab,
} from 'types/insuranceBilling';
import { getBillingBoard } from 'api/insuranceBilling';
import { formatDateMDY, formatMoney, formatTimestampMDY, rowStripe } from './statusConfig';
import {
    activeCheckedCount,
    CheckFilterDropdown,
    isCheckFilterActive,
    NONE_SELECTED,
    SearchFilterDropdown,
} from './ColumnFilters';
import type { CheckFilterOption } from './ColumnFilters';
import AttentionCards from './AttentionCards';
import SegmentChips from './SegmentChips';
import IssuesCell from './IssuesCell';
import StatusBadge from './StatusBadge';
import RemittanceDoc from './RemittanceDoc';
import s from './s.module.less';

// 表头可排序的列（服务端还支持 patient / status / payer，原型没给这几列排序）
const SORTABLE: TBoardSort[] = ['dos', 'billed', 'paid', 'submitted'];

type Props = {
    apiScope: TBillingApiScope;
    showProviderCol: boolean;
    isSelfProvider: (providerId?: number | null) => boolean;
    // 计数、NEEDS ATTENTION、筛选选项；由容器拉取（waiting 态的首批面板也要用）。null = 还没回来
    summary: BoardSummaryVo | null;
    // 服务日期窗口（YYYY-MM-DD，含起止日），跟随 KPI 卡片的期间选择；undefined = 不限
    dosFrom?: string;
    dosTo?: string;
    // 变化即重拉当前页（如打开 visit 时发现已出 claim）
    refreshKey: number;
    onOpenClaim: (id: number) => void;
    onOpenVisit: (encounterId: number) => void;
};

// 勾选清单 → 接口 CSV：[] = 没选过（不限制，undefined）；[NONE_SELECTED] = 明确全部取消（null，空表）
const checkSelToParam = (sel: string[]): string | undefined | null => {
    if (sel.includes(NONE_SELECTED)) return null;
    return sel.length ? sel.join(',') : undefined;
};

// claim 与还没变成 claim 的 visit 混排在一张表（/claims/board）：tab、提醒卡、列筛选、排序、分页
// 全部交给服务端，前端不重算状态。tab / 提醒卡上的数字是 summary 的口径（随 KPI 期间），不随列筛选变化。
const ClaimsTab = ({
    apiScope,
    showProviderCol,
    isSelfProvider,
    summary,
    dosFrom,
    dosTo,
    refreshKey,
    onOpenClaim,
    onOpenVisit,
}: Props) => {
    const [rows, setRows] = useState<TBoardRow[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    // 请求失败与「成功但没有匹配」分开：失败不能显示成空表
    const [failed, setFailed] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    // PATIENT 表头搜索（接口 patient 参数）
    const [patientInput, setPatientInput] = useState('');
    const [patientSearch, setPatientSearch] = useState('');
    const [patientFilterOpen, setPatientFilterOpen] = useState(false);
    // PROVIDER 表头搜索（接口 providerName 参数，仅 Practice）
    const [providerNameInput, setProviderNameInput] = useState('');
    const [providerName, setProviderName] = useState('');
    const [providerFilterOpen, setProviderFilterOpen] = useState(false);
    // 三个勾选清单：[] = 没选过（等于全勾、不筛选），[NONE_SELECTED] = 全部取消（空表）
    const [stateSel, setStateSel] = useState<string[]>([]);
    const [statusFilterOpen, setStatusFilterOpen] = useState(false);
    const [payerSel, setPayerSel] = useState<string[]>([]);
    const [payerFilterOpen, setPayerFilterOpen] = useState(false);
    const [sourceSel, setSourceSel] = useState<string[]>([]);
    const [sourceFilterOpen, setSourceFilterOpen] = useState(false);
    const [tab, setTab] = useState<TBoardTab>('all');
    // 被激活的 NEEDS ATTENTION 卡片：tab=attention + card
    const [card, setCard] = useState<TAttentionCard | null>(null);
    // 原型默认按服务日期排，这样 visit 与 claim 自然交错
    const [sortBy, setSortBy] = useState<TBoardSort>('dos');
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
    // REMITTANCE 列：835 文档页只展示这张 claim（非 null 即打开）
    const [openRemit, setOpenRemit] = useState<{ paymentId: number; claimId: number } | null>(null);

    // 筛选选项来自 summary.filters（随 scope 与 KPI 期间变化，不因列筛选缩小）。
    // STATUS 带条数（原型逐状态计数）；SOURCE 显示 label、提交 key；PAYER 按名字
    const statusOptions: CheckFilterOption[] = useMemo(
        () => (summary?.filters.states || []).map((o) => ({ key: o.key, label: o.label, count: o.count })),
        [summary],
    );
    const payerOptions: CheckFilterOption[] = useMemo(
        () => (summary?.filters.payers || []).map((o) => ({ key: o.name, label: o.name })),
        [summary],
    );
    const sourceOptions: CheckFilterOption[] = useMemo(
        () => (summary?.filters.sources || []).map((o) => ({ key: o.key, label: o.label })),
        [summary],
    );

    // 两个表头搜索各 300ms 防抖；生效时重置回第一页
    useEffect(() => {
        const timer = setTimeout(() => {
            setPatientSearch(patientInput);
            setPage(1);
        }, 300);
        return () => clearTimeout(timer);
    }, [patientInput]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setProviderName(providerNameInput);
            setPage(1);
        }, 300);
        return () => clearTimeout(timer);
    }, [providerNameInput]);

    // KPI 期间变了：回到第一页（旧页码在新窗口里可能已越界）
    useEffect(() => {
        setPage(1);
    }, [dosFrom, dosTo]);

    const stateParam = checkSelToParam(stateSel);
    const payerParam = checkSelToParam(payerSel);
    const sourceParam = checkSelToParam(sourceSel);

    useEffect(() => {
        // 任一列明确全部取消：不请求，直接空表（cleanup 已让在途的旧请求作废）
        if (stateParam === null || payerParam === null || sourceParam === null) {
            setRows([]);
            setTotal(0);
            setFailed(false);
            setLoading(false);
            return undefined;
        }
        const query: BoardQuery = { tab, page, limit: pageSize, sortBy, sortDir };
        if (stateParam) query.state = stateParam;
        if (payerParam) query.payer = payerParam;
        if (sourceParam) query.source = sourceParam;
        if (card) query.card = card;
        if (dosFrom) query.dosFrom = dosFrom;
        if (dosTo) query.dosTo = dosTo;
        if (patientSearch.trim()) query.patient = patientSearch.trim();
        // providerName 只有 Practice 接受，My 不传
        if (showProviderCol && providerName.trim()) query.providerName = providerName.trim();

        let cancelled = false;
        setLoading(true);
        (async () => {
            const res: any = await getBillingBoard(query, apiScope);
            if (cancelled) return;
            const vo: BoardPageVo | null = (!res?.error && res?.data?.data) || null;
            setFailed(!vo);
            setRows(vo?.data || []);
            setTotal(vo?.meta?.total || 0);
            setLoading(false);
        })();
        return () => {
            cancelled = true;
        };
    }, [
        tab,
        card,
        page,
        pageSize,
        patientSearch,
        stateParam,
        payerParam,
        sourceParam,
        providerName,
        showProviderCol,
        sortBy,
        sortDir,
        apiScope,
        dosFrom,
        dosTo,
        refreshKey,
    ]);

    const filtersActive =
        tab !== 'all' ||
        !!card ||
        !!patientSearch.trim() ||
        !!providerName.trim() ||
        stateSel.length > 0 ||
        payerSel.length > 0 ||
        sourceSel.length > 0;

    // Clear filters：回到 All、清掉卡片与所有列筛选，保留权限 scope
    const clearAllFilters = () => {
        setTab('all');
        setCard(null);
        setPatientInput('');
        setProviderNameInput('');
        setStateSel([]);
        setPayerSel([]);
        setSourceSel([]);
        setPage(1);
    };

    // 卡片点击：tab=attention + card；再点一次恢复 All
    const toggleAttentionCard = (key: TAttentionCard) => {
        if (card === key) {
            setCard(null);
            setTab('all');
        } else {
            setCard(key);
            setTab('attention');
        }
        setPage(1);
    };

    // 分页由下面的 Pagination 自己管，这里只接排序
    const handleTableChange = (
        _pagination: TablePaginationConfig,
        _filters: Record<string, FilterValue | null>,
        sorter: SorterResult<TBoardRow> | SorterResult<TBoardRow>[],
    ) => {
        const one = Array.isArray(sorter) ? sorter[0] : sorter;
        if (one?.order && SORTABLE.includes(one.columnKey as TBoardSort)) {
            setSortBy(one.columnKey as TBoardSort);
            setSortDir(one.order === 'ascend' ? 'asc' : 'desc');
        } else {
            // 取消排序回到默认 dos desc（原型默认序）
            setSortBy('dos');
            setSortDir('desc');
        }
        setPage(1);
    };

    const sortOrder = (key: TBoardSort) => {
        if (sortBy !== key) return null;
        return sortDir === 'asc' ? 'ascend' : 'descend';
    };

    // 表头漏斗：生效时变蓝并标出勾了几个（credentialing 表格的「漏斗 + 数字」）
    const funnelIcon = (selected: string[], options: CheckFilterOption[]) => {
        const active = isCheckFilterActive(selected);
        if (!active) return <FilterFilled />;
        return (
            <span className={s.thFilterOn}>
                <FilterFilled />
                <span className={s.thFilterCount}>{activeCheckedCount(selected, options)}</span>
            </span>
        );
    };

    // 详情接口按 kind 选：visit 走 encounterId，claim 走 claimId
    const openRow = (row: TBoardRow) =>
        row.kind === 'visit' ? onOpenVisit(row.encounterId) : onOpenClaim(row.claimId);

    const columns = useMemo(() => {
        const cols: ColumnsType<TBoardRow> = [
            {
                title: 'CLAIM ID',
                key: 'id',
                // visit 还不是 claim，没有单号可显示（不拿 encounterId 冒充）
                render: (_: unknown, row: TBoardRow) =>
                    row.kind === 'visit' ? <span className={s.mutCell}>—</span> : `#${row.claimId}`,
            },
            {
                title: 'PATIENT',
                key: 'patient',
                filteredValue: patientSearch ? ['patient'] : null,
                filterIcon: <SearchOutlined />,
                filterDropdownOpen: patientFilterOpen,
                onFilterDropdownOpenChange: setPatientFilterOpen,
                filterDropdown: () => (
                    <SearchFilterDropdown
                        placeholder="Search patients..."
                        value={patientInput}
                        onChange={setPatientInput}
                    />
                ),
                render: (_: unknown, row: TBoardRow) => <span className={s.patientCell}>{row.patient}</span>,
            },
            // STATUS 与 PAID 提到 PATIENT 之后：provider 最先扫的就是「什么阶段」和「付没付」
            {
                title: 'STATUS',
                key: 'status',
                filteredValue: isCheckFilterActive(stateSel) ? ['status'] : null,
                filterIcon: () => funnelIcon(stateSel, statusOptions),
                filterDropdownOpen: statusFilterOpen,
                onFilterDropdownOpenChange: setStatusFilterOpen,
                filterDropdown: () => (
                    <CheckFilterDropdown
                        options={statusOptions}
                        selected={stateSel}
                        onChange={(next) => {
                            setStateSel(next);
                            setPage(1);
                        }}
                        emptyText="No statuses yet"
                    />
                ),
                render: (_: unknown, row: TBoardRow) => <StatusBadge state={row.state} />,
            },
            {
                title: 'ISSUES',
                key: 'issue',
                render: (_: unknown, row: TBoardRow) => <IssuesCell issues={row.issues} />,
            },
            {
                title: 'PAID',
                key: 'paid',
                sorter: true,
                sortOrder: sortOrder('paid'),
                render: (_: unknown, row: TBoardRow) => <span className={s.mutCell}>{formatMoney(row.paid)}</span>,
            },
        ];
        if (showProviderCol) {
            cols.push({
                title: 'PROVIDER',
                key: 'provider',
                filteredValue: providerName ? ['provider'] : null,
                filterIcon: <SearchOutlined />,
                filterDropdownOpen: providerFilterOpen,
                onFilterDropdownOpenChange: setProviderFilterOpen,
                filterDropdown: () => (
                    <SearchFilterDropdown
                        placeholder="Search providers..."
                        value={providerNameInput}
                        onChange={setProviderNameInput}
                    />
                ),
                render: (_: unknown, row: TBoardRow) => (
                    <span>
                        {row.providerName || '—'}
                        {isSelfProvider(row.providerId) && <span className={s.youPill}>You</span>}
                    </span>
                ),
            });
        }
        cols.push(
            {
                title: 'PAYER',
                key: 'payer',
                filteredValue: isCheckFilterActive(payerSel) ? ['payer'] : null,
                filterIcon: () => funnelIcon(payerSel, payerOptions),
                filterDropdownOpen: payerFilterOpen,
                onFilterDropdownOpenChange: setPayerFilterOpen,
                filterDropdown: () => (
                    <CheckFilterDropdown
                        options={payerOptions}
                        selected={payerSel}
                        onChange={(next) => {
                            setPayerSel(next);
                            setPage(1);
                        }}
                        emptyText="No payers yet"
                    />
                ),
                render: (_: unknown, row: TBoardRow) => row.payer || <span className={s.mutCell}>—</span>,
            },
            {
                title: 'DATE OF SERVICE',
                key: 'dos',
                dataIndex: 'dos',
                sorter: true,
                sortOrder: sortOrder('dos'),
                render: (v?: string | null) => <span className={s.mutCell}>{formatDateMDY(v)}</span>,
            },
            {
                title: 'BILLED',
                key: 'billed',
                sorter: true,
                sortOrder: sortOrder('billed'),
                render: (_: unknown, row: TBoardRow) => <span className={s.mutCell}>{formatMoney(row.billed)}</span>,
            },
            {
                title: 'SUBMITTED',
                key: 'submitted',
                sorter: true,
                sortOrder: sortOrder('submitted'),
                render: (_: unknown, row: TBoardRow) => (
                    <span className={s.mutCell}>{formatTimestampMDY(row.submitted)}</span>
                ),
            },
            // SOURCE 挪到最后：来自哪个 EHR 是排查时才看的信息
            {
                title: 'SOURCE',
                key: 'source',
                filteredValue: isCheckFilterActive(sourceSel) ? ['source'] : null,
                filterIcon: () => funnelIcon(sourceSel, sourceOptions),
                filterDropdownOpen: sourceFilterOpen,
                onFilterDropdownOpenChange: setSourceFilterOpen,
                filterDropdown: () => (
                    <CheckFilterDropdown
                        options={sourceOptions}
                        selected={sourceSel}
                        onChange={(next) => {
                            setSourceSel(next);
                            setPage(1);
                        }}
                        emptyText="No sources yet"
                    />
                ),
                render: (_: unknown, row: TBoardRow) => <span className={s.sourceCell}>{row.source || '—'}</span>,
            },
            // 倒数第二列，和 View 一起固定在右侧不随表格横向滚动。只放文档图标，点开查看 835 明细；
            // stopPropagation 防止冒泡到整行点击打开 claim 抽屉
            {
                title: 'REMITTANCE',
                key: 'remittance',
                fixed: 'right',
                width: 120,
                align: 'center',
                render: (_: unknown, row: TBoardRow) => {
                    if (row.kind !== 'claim' || row.remittancePaymentId == null) {
                        return <span className={s.mutCell}>—</span>;
                    }
                    const paymentId = row.remittancePaymentId;
                    return (
                        <Button
                            type="link"
                            className={s.remitIconBtn}
                            icon={<FileTextOutlined />}
                            aria-label="View remittance"
                            onClick={(e) => {
                                e.stopPropagation();
                                setOpenRemit({ paymentId, claimId: row.claimId });
                            }}
                        />
                    );
                },
            },
            {
                title: '',
                key: 'action',
                fixed: 'right',
                width: 84,
                align: 'center',
                render: (_: unknown, row: TBoardRow) => (
                    <Button type="link" className={s.viewLink} onClick={() => openRow(row)}>
                        View
                    </Button>
                ),
            },
        );
        return cols;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        showProviderCol,
        sortBy,
        sortDir,
        stateSel,
        statusOptions,
        statusFilterOpen,
        payerSel,
        payerOptions,
        payerFilterOpen,
        sourceSel,
        sourceOptions,
        sourceFilterOpen,
        patientSearch,
        patientInput,
        patientFilterOpen,
        providerName,
        providerNameInput,
        providerFilterOpen,
    ]);

    const emptyText = () => {
        if (failed) return "We couldn't load claims. Refresh the page to try again.";
        // 无任何筛选的空表（如 owner 的 My billing 本人 0 条）不说"筛选没匹配"
        return filtersActive ? 'No claims match your filters.' : 'No claims yet.';
    };

    return (
        <div>
            <AttentionCards cards={summary?.attention || []} activeKey={card} onToggle={toggleAttentionCard} />
            <SegmentChips
                counts={summary?.tabs || null}
                active={tab}
                onChange={(key) => {
                    setTab(key);
                    setCard(null);
                    // 分段是粗切，换段时不保留上一段的 STATUS 勾选（否则交集常常为空表）
                    setStateSel([]);
                    setPage(1);
                }}
                showClear={filtersActive}
                onClear={clearAllFilters}
            />
            <div className={s.tablePanel}>
                <Table<TBoardRow>
                    rowKey="key"
                    columns={columns}
                    dataSource={rows}
                    loading={loading}
                    showSorterTooltip={false}
                    onChange={handleTableChange}
                    locale={{ emptyText: emptyText() }}
                    pagination={false}
                    // 需要 provider 处理的行带左侧色条：payer 退回的 claim 红色，建不出 claim 的 visit 琥珀色
                    rowClassName={(row) => {
                        const stripe = rowStripe(row.state);
                        if (stripe === 'red') return s.attnRowRed;
                        if (stripe === 'amber') return s.attnRowAmber;
                        return '';
                    }}
                    onRow={(row) => ({ onClick: () => openRow(row) })}
                    scroll={{ x: 'max-content' }}
                />
                {total > 0 && (
                    <div className={s.tablePager}>
                        <Pagination
                            current={page}
                            pageSize={pageSize}
                            total={total}
                            showSizeChanger
                            onChange={(p, ps) => {
                                setPage(p);
                                setPageSize(ps);
                            }}
                        />
                    </div>
                )}
            </div>
            {openRemit && (
                <RemittanceDoc
                    paymentId={openRemit.paymentId}
                    claimId={openRemit.claimId}
                    apiScope={apiScope}
                    onClose={() => setOpenRemit(null)}
                />
            )}
        </div>
    );
};

export default ClaimsTab;
